import { aaveV4Spokes, parseAaveV4SpokeLenderKey } from '@1delta/data-sdk'

/**
 * Number of base calls per reserve (excluding dynamic config calls):
 *  0: getReserve(reserveId)
 *  1: getReserveConfig(reserveId)
 *  2: getReserveSuppliedAssets(reserveId)
 *  3: getReserveDebt(reserveId)       → (drawn, premium)
 *  4: getReserveTotalDebt(reserveId)
 *
 * Plus getDynamicReserveConfig(reserveId, key) for each key 0..dynamicConfigKeyMax
 */
export const BASE_CALLS_PER_RESERVE = 5

/**
 * Number of trailing calls per Spoke (after all reserve calls):
 *  0: getLiquidationConfig()
 *
 * The spoke ORACLE() is read from `aaveV4Spokes()` metadata, not on-chain.
 */
export const CALLS_PER_SPOKE_TAIL = 1

/**
 * Max hub assets to enumerate per distinct hub.
 * Hub calls per asset: getAsset, getAddedAssets, getAssetTotalOwed (3 calls).
 * Plus 1 getAssetCount call. Total per hub = 1 + MAX * 3.
 */
export const MAX_HUB_ASSETS = 25

/** Helper: is address valid (not empty, not "0x") */
function isValidAddress(addr: string | undefined): boolean {
  return !!addr && addr !== '0x' && addr.length > 2
}

/** Get the number of dynamic config keys to fetch for a spoke (0..max inclusive) */
function getDynamicConfigCount(spokeEntry: any): number {
  const max = spokeEntry?.dynamicConfigKeyMax ?? 0
  return max + 1
}

/**
 * Resolve the spoke entry referenced by a per-spoke V4 lender key
 * (`AAVE_V4_<HEX>`). Returns `undefined` if the key isn't a per-spoke key
 * or if no entry exists for the chain/spoke.
 */
function resolveSpokeEntry(chainId: string, lender: string) {
  const parsed = parseAaveV4SpokeLenderKey(lender)
  if (!parsed) return undefined
  return aaveV4Spokes()?.[chainId]?.[parsed.spokeAddrLower]
}

/**
 * Collect distinct lowercase hub addresses across the spoke's reserves,
 * preserving first-seen order so the parser can mirror the call layout.
 */
function distinctHubsInOrder(reserves: any[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const r of reserves) {
    const h = (r?.hub ?? '').toLowerCase()
    if (!h || !isValidAddress(h)) continue
    if (seen.has(h)) continue
    seen.add(h)
    out.push(h)
  }
  return out
}

/**
 * Builds multicall call list for Aave V4 public data — single spoke only.
 *
 * The lender argument is a per-spoke synthesized key (`AAVE_V4_<HEX>`).
 * The spoke address inside the key is the venue identity; per-reserve hub
 * binding lives on each reserve entry in the spoke's metadata.
 *
 * Call layout:
 *   For each reserve in this spoke:
 *     [getReserve, getReserveConfig, getReserveSuppliedAssets,
 *      getReserveDebt, getReserveTotalDebt]
 *     [getDynamicReserveConfig(rid, 0), ..., getDynamicReserveConfig(rid, max)]
 *   getLiquidationConfig() (spoke-level tail)
 *   getReservesPrices(reserveIds)  (spoke oracle batch, if oracle valid)
 *   For each distinct hub h (in first-seen order over the reserve list):
 *     h.getAssetCount()
 *     For i=0..MAX_HUB_ASSETS-1:
 *       h.getAsset(i), h.getAddedAssets(i), h.getAssetTotalOwed(i)
 *     For each reserve r where r.hub == h:
 *       h.getSpoke(r.assetId, spokeAddr)
 */
export const buildAaveV4LenderReserveCall = (
  chainId: string,
  lender: string,
) => {
  const spokeEntry = resolveSpokeEntry(chainId, lender)
  if (!spokeEntry) return []

  const spokeAddr = spokeEntry.spoke.toLowerCase()
  if (!isValidAddress(spokeAddr)) return []

  const reserves = spokeEntry.reserves ?? []
  const dynConfigCount = getDynamicConfigCount(spokeEntry)

  const calls: any[] = []

  // Per-reserve calls
  for (const r of reserves) {
    const rid = r.reserveId
    calls.push(
      { address: spokeAddr, name: 'getReserve', params: [rid] },
      { address: spokeAddr, name: 'getReserveConfig', params: [rid] },
      { address: spokeAddr, name: 'getReserveSuppliedAssets', params: [rid] },
      { address: spokeAddr, name: 'getReserveDebt', params: [rid] },
      { address: spokeAddr, name: 'getReserveTotalDebt', params: [rid] },
    )
    for (let key = 0; key < dynConfigCount; key++) {
      calls.push({
        address: spokeAddr,
        name: 'getDynamicReserveConfig',
        params: [rid, key],
      })
    }
  }

  // Spoke-level tail call
  calls.push({
    address: spokeAddr,
    name: 'getLiquidationConfig',
    params: [],
  })

  // Oracle price batch (single batch for all reserves on this spoke)
  const reserveIds = reserves.map((r: any) => r.reserveId)
  if (reserveIds.length > 0 && isValidAddress(spokeEntry.oracle)) {
    calls.push({
      address: spokeEntry.oracle.toLowerCase(),
      name: 'getReservesPrices',
      params: [reserveIds],
    })
  }

  // Hub enumeration: per distinct hub. The same iteration order is mirrored
  // by the parser so we don't have to tag offsets explicitly.
  const distinctHubs = distinctHubsInOrder(reserves)
  for (const hub of distinctHubs) {
    calls.push({ address: hub, name: 'getAssetCount', params: [] })
    for (let i = 0; i < MAX_HUB_ASSETS; i++) {
      calls.push(
        { address: hub, name: 'getAsset', params: [i] },
        { address: hub, name: 'getAddedAssets', params: [i] },
        { address: hub, name: 'getAssetTotalOwed', params: [i] },
      )
    }
    // Spoke binding for every reserve attached to this hub.
    for (const r of reserves) {
      if ((r?.hub ?? '').toLowerCase() !== hub) continue
      const assetId = r.assetId
      if (typeof assetId !== 'number') continue
      calls.push({
        address: hub,
        name: 'getSpoke',
        params: [assetId, spokeAddr],
      })
    }
  }

  return calls
}

/**
 * Computes the expected number of calls for the parser to validate.
 */
export const getAaveV4ExpectedCallCount = (
  chainId: string,
  lender: string,
): number => {
  const spokeEntry = resolveSpokeEntry(chainId, lender)
  if (!spokeEntry) return 0
  if (!isValidAddress(spokeEntry.spoke)) return 0

  const reserves = spokeEntry.reserves ?? []
  const dynConfigCount = getDynamicConfigCount(spokeEntry)

  let count = 0

  // per-reserve: base + dynamic configs
  count += reserves.length * (BASE_CALLS_PER_RESERVE + dynConfigCount)

  // spoke tail
  count += CALLS_PER_SPOKE_TAIL

  // oracle batch
  if (reserves.length > 0 && isValidAddress(spokeEntry.oracle)) {
    count += 1
  }

  // distinct hubs: per-hub fixed cost + per-reserve spoke binding
  const distinctHubs = distinctHubsInOrder(reserves)
  for (const hub of distinctHubs) {
    count += 1 + MAX_HUB_ASSETS * 3
    for (const r of reserves) {
      if ((r?.hub ?? '').toLowerCase() !== hub) continue
      if (typeof r.assetId === 'number') count += 1
    }
  }

  return count
}
